import React, {useEffect, useState} from 'react';
import {NativeStackScreenProps} from '@react-navigation/native-stack';
import {RootNavigatorParams, Routes} from 'navigation/Routes';
import {TextStyle, View, ViewStyle, ScrollView} from 'react-native';
import colors from 'theme/Colors';
import {spacing} from 'theme/Units';
import {updateProjectRequest, resetMessage} from 'redux/slices/projectSlice';
import {getOrgID} from 'redux/selectors/authSelector';
import {
  selectedProjectData,
  projectFailure,
  projectSuccess,
} from 'redux/selectors/projectSelector';
import {ProjectForm} from 'components';
import {useDispatch, useSelector} from 'react-redux';
import {useToast} from 'hooks';
import {ERROR_KEY} from 'constants/keys';
import {Text} from 'ui-kits';

import {
  ErrorCodes,
  getErrorMessage,
} from 'utils/getMessages';
import { getFormattedDate } from 'utils/getFormattedDate';

type initialValuesType = {
  proj_id: string;
  name: string;
  description: string;
  start_date: Date | null;
  end_date: Date | null;
  sprint_duration: string;
  id: number | null;
};

const initialValues: initialValuesType = {
  proj_id: '',
  id: null,
  name: '',
  description: '',
  start_date: null,
  end_date: null,
  sprint_duration: '',
};

export function UpdateProject({
  navigation,
}: NativeStackScreenProps<RootNavigatorParams>) {
  const dispatch = useDispatch();
  const org_id = useSelector(getOrgID);
  const updateProjectError = useSelector(projectFailure);
  const updateProjectSuccess = useSelector(projectSuccess);
  const {showToasts} = useToast();
  const selectedProject = useSelector(selectedProjectData);
  const [formValues, setFormValues] =
    useState<initialValuesType>(initialValues);

  useEffect(() => {
    if (updateProjectError) {
      showToasts(
        ERROR_KEY,
        getErrorMessage(updateProjectError as ErrorCodes),
        'error',
      );
      dispatch(resetMessage());
    }
  }, [dispatch, showToasts, updateProjectError]);

  // success message toast
  useEffect(() => {
    if (updateProjectSuccess) {
      if (updateProjectSuccess === 'S-10016') {
        navigation.navigate(Routes.PROJECT_DETAILS, {
          id: selectedProject?.id?.toString()!,
        });
      }
      dispatch(resetMessage());
    }
  }, [dispatch, showToasts, updateProjectSuccess, navigation, selectedProject]);

  useEffect(() => {
    if (selectedProject) {
      setFormValues({
        ...selectedProject,
        proj_id: selectedProject.proj_id,
        start_date: selectedProject.start_date ? new Date(selectedProject.start_date) : null,
        end_date: selectedProject.end_date ? new Date(selectedProject.end_date) : null,
        sprint_duration: selectedProject.sprint_duration?.toString() || '',
      });
    }
  }, [selectedProject]);

  const handleCancel = () => {
    navigation.navigate(Routes.PROJECT_DETAILS, {
      id: selectedProject?.id.toString()!,
    });
  };

  const submitHandler = ({id, ...values}: initialValuesType) => {
    const payload = {
      ...values,
      start_date:values.start_date ? getFormattedDate(values.start_date, 'yyyy-mm-dd', '-') : null,
      end_date: values.end_date ? getFormattedDate(values.end_date, 'yyyy-mm-dd', '-') : null,
      org_id,
      id,
      sprint_duration: Number(values.sprint_duration) || 0,
    };
    dispatch(updateProjectRequest(payload as any));
  };

  return (
    <View style={styles.container}>
      <ScrollView
        showsVerticalScrollIndicator={false}
        style={styles.formWrapper}>
        <Text style={styles.heading}>Update Project</Text>
        <ProjectForm
          initialValues={formValues}
          onSubmit={submitHandler}
          onCancel={handleCancel}
          isEditing={true}
        />
      </ScrollView>
    </View>
  );
}

type styleType = {
  container: ViewStyle;
  formWrapper: ViewStyle;
  heading: TextStyle;
};

const styles: styleType = {
  container: {
    alignItems: 'center',
    backgroundColor: colors.palette.white,
    flex: 1,
  },
  formWrapper: {
    maxWidth: 700,
    width: '100%',
    paddingVertical: spacing['5'],
  },
  heading: {
    color: colors.palette.brandBlue,
    fontWeight: 600,
    fontSize: 40,
  },
};
